import { Button } from '@/components/Atoms/Button/Button';
import { Details, Summary } from '@/components/Atoms/Details/Details';
import { Number } from '@/components/Atoms/Number/Number';
import { Dialog } from '@/components/Molecules/Dialog/Dialog';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { ContentItemCard } from '@/components/Organisms/ContentItemCard/ContentItemCard';
import useTranslation from '@/hooks/useTranslation';
import { useTruncatedList } from '@/hooks/useTruncatedList';
import type { IContentItem, IPerson } from '@/interfaces';
import { TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import classNames from '@/utils/classNames';
import type { FC, ReactNode } from 'react';
import { useId } from 'react';
import type { FCl } from 'react-fc';
import styles from './RelatedSidebar.module.css';

interface IRelatedList {
    title: string;
    totalCount?: number;
    items: any;
    itemRenderer: (item: any, index: number) => ReactNode;
    // if set, will link out to another page. if not set, will use a dialog
    seeMoreUrl?: string;
}

interface IProps {
    open?: boolean;
    lists: IRelatedList[];
}

export const RelatedSidebar: FCl<IProps> = ({ lists, className, open }) => {
    const name = useId();
    lists = (lists || []).filter(Boolean);
    if (lists.length < 1) {
        return null;
    }
    return (
        <div className={classNames(styles.content, className)}>
            {lists.map((relatedContentItem, i) => (
                <Details key={relatedContentItem.title} name={open ? undefined : name} open={open || i === 0}>
                    <Summary className="cds-t-h4">
                        {relatedContentItem.title}
                        {relatedContentItem.totalCount && (
                            <>
                                {' '}
                                (<Number value={relatedContentItem.totalCount} />)
                            </>
                        )}
                    </Summary>
                    <div className={styles.detailsContent}>
                        {relatedContentItem.seeMoreUrl ? (
                            <OnwardList list={relatedContentItem} />
                        ) : (
                            <OnPageList list={relatedContentItem} />
                        )}
                    </div>
                </Details>
            ))}
        </div>
    );
};

export const sidebarContentCardRenderer =
    (analyticsBaseId: string, cardProps = {}) =>
    (item: IContentItem, index: number) => (
        <ContentItemCard
            item={item}
            analyticsCategory={TrackingCategory.RelatedContent}
            analyticsBaseId={`${analyticsBaseId}: Content: Card ${index}`}
            headingLevel="h3"
            withImage={false}
            {...cardProps}
        />
    );

const OnPageList: FC<{ list: IRelatedList }> = ({ list }) => {
    const { t } = useTranslation();
    const { dialog, truncated, itemsToRender, totalCount } = useTruncatedList(list.items);

    const listRenderer = (listItems: IPerson[]) => (
        <Stack as="ul">
            {listItems.map((item: any, index: number) => (
                <li key={item.url ?? index}>{list.itemRenderer(item, index)}</li>
            ))}
        </Stack>
    );

    return (
        <>
            {listRenderer(itemsToRender)}
            {truncated && (
                <Dialog title={list.title} {...dialog}>
                    {listRenderer(list.items)}
                </Dialog>
            )}
            {truncated && (
                <div className={styles.seeMore}>
                    <Button kind="tertiary" onClick={() => dialog.open()}>
                        {t('Actions.show_more')} ({totalCount})
                    </Button>
                </div>
            )}
        </>
    );
};

const OnwardList: FC<{ list: IRelatedList }> = ({ list }) => {
    const { t } = useTranslation();
    const showMoreLink = list.seeMoreUrl && (list.totalCount === undefined || list.totalCount > list.items.length);
    return (
        <>
            <Stack as="ul">
                {list.items.map((item: any, index: number) => (
                    <li key={item.url}>{list.itemRenderer(item, index)}</li>
                ))}
            </Stack>
            {showMoreLink && (
                <div className={styles.seeMore}>
                    <a href={list.seeMoreUrl} className="cds-button cds-button--tertiary">
                        <span>
                            {t('Actions.see_more')}
                            {list.totalCount && (
                                <>
                                    {' '}
                                    (<Number value={list.totalCount} />)
                                </>
                            )}
                        </span>
                    </a>
                </div>
            )}
        </>
    );
};
